// Phase 9.4.B — Portal invoice detail + Pay Now (2026-05-11 #3).
//
// Spec: docs/apps/client-portal.md § "Invoices" + § "Outstanding invoices".
//
// Read-only view of one invoice + the Pay Now CTA. Pay Now POSTs to
// /portal/invoices/:id/pay, gets back a Stripe Payment Link URL, and
// redirects via window.location.assign — Stripe handles the rest. The
// existing webhook flips the invoice to paid; the user can return to
// the portal and see the new status.
//
// CEO 2026-10-19: the page lists the invoice's items the way the client's
// invoice document (/i/:uuid, apps/web PublicInvoicePage) does — same lines,
// same per-row and per-line money math — and has a "Download PDF" button.
// Flow has no server-side PDF renderer: every client document's "Download as
// PDF" is the browser's print-to-PDF over print styles, so this page does the
// same (PRINT_STYLES below hides the portal menu + top bar and the buttons).
// When card payment is off, the pay panel just says to reply to the account
// manager — no ACH / check / wire jargon.
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import {
  type PortalInvoiceLine,
  type PortalInvoiceLineVariant,
  type UnpayableReason,
  BUCKET_LABEL,
  BUCKET_PILL_CLASS,
  PAYMENT_METHOD_LABEL,
  STATUS_LABEL,
  STATUS_PILL_CLASS,
  UNPAYABLE_REASON_LABEL,
  fetchPortalInvoice,
  formatDate,
  formatUsd,
  outstandingForInvoice,
  startPortalInvoicePayment,
} from "@/lib/portal-invoices";
import { termsLabel } from "@/lib/portal-store";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

const OPEN_STATUSES = new Set(["open", "sent_to_client", "exported"]);

// Print-to-PDF: the document only. Mirrors apps/web's shared
// DocumentPrintStyles (colour kept, rows never split across pages); the
// portal frame's sidebar (<aside>) and sticky top bar are dropped here because
// PortalLayout is shared — this <style> only exists while this page is open.
const PRINT_STYLES = `
  @media print {
    @page { margin: 0.5in; }
    html, body {
      background: #ffffff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    aside, header.sticky { display: none !important; }
    main { padding: 0 !important; max-width: 100% !important; }
    li, tr, article { break-inside: avoid; }
  }
`;

function lineImageUrl(line: PortalInvoiceLine): string | null {
  const images = line.productSnapshot?.imageUrls;
  if (Array.isArray(images) && images.length > 0) {
    return typeof images[0] === "string" ? images[0] : null;
  }
  if (images && typeof images === "object") {
    const first = Object.values(images)[0];
    return typeof first === "string" ? first : null;
  }
  return null;
}

function variantLabel(v: PortalInvoiceLineVariant): string {
  const parts: string[] = [];
  if (v.size) parts.push(`Size: ${v.size}`);
  if (v.color) parts.push(`Color: ${v.color}`);
  if (v.material) parts.push(v.material);
  if (parts.length === 0) return "Standard";
  return parts.join(" · ");
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [payStarting, setPayStarting] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["portal-invoice", id],
    queryFn: () => fetchPortalInvoice(id!),
    enabled: typeof id === "string" && id.length > 0,
  });

  if (!user) return null;

  const notFound = isError && error instanceof ApiError && error.status === 404;
  // Portal v2 Slice A — the payable figure is the NETTED outstanding
  // (shared formula; deposits + ledgered payments subtracted), never the
  // raw balance_due column, so this page always agrees with the dashboard
  // Action Center. The server recomputes the charge amount independently.
  const outstanding = data !== undefined ? outstandingForInvoice(data) : "0";
  const paymentsReceived = (data?.payments ?? [])
    .filter((p) => p.status === "succeeded")
    .reduce((sum, p) => sum + Number.parseFloat(p.amount || "0"), 0);
  // Money still owed on an open invoice — the only time a pay panel shows.
  // Nothing outstanding → no panel at all (never "reply to pay" for $0).
  const owes =
    data !== undefined &&
    OPEN_STATUSES.has(data.status) &&
    Number.parseFloat(outstanding) > 0;
  const canPay = owes && data.allowCreditCard !== false;

  async function onPayClick(): Promise<void> {
    if (!id) return;
    setPayError(null);
    setPayStarting(true);
    try {
      const link = await startPortalInvoicePayment(id);
      window.location.assign(link.url);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { reason?: UnpayableReason } | null;
        if (err.status === 409 && body?.reason) {
          setPayError(UNPAYABLE_REASON_LABEL[body.reason]);
        } else if (err.status === 404) {
          setPayError("This invoice is no longer available.");
        } else {
          setPayError("Something went wrong starting your payment. Try again.");
        }
      } else {
        setPayError("Something went wrong starting your payment. Try again.");
      }
      setPayStarting(false);
    }
  }

  return (
    <PortalLayout>
        <style>{PRINT_STYLES}</style>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
          <Link
            to="/invoices"
            className="inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
          >
            ← All invoices
          </Link>
          {data && (
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex min-h-[40px] items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas"
            >
              Download PDF
            </button>
          )}
        </div>

        {isLoading && (
          <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center text-brown-300 shadow-sm">
            Loading invoice…
          </div>
        )}

        {notFound && (
          <div className="rounded-lg border border-cream-200 bg-white px-6 py-16 text-center shadow-sm">
            <p className="mb-2 font-display text-2xl font-bold tracking-tight text-brown-500">
              Invoice not found
            </p>
            <p className="mb-6 text-brown-400">
              This invoice doesn't exist, or it's not part of your account.
            </p>
            <Link
              to="/invoices"
              className="inline-block rounded-md bg-ink px-6 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700"
            >
              Back to invoices
            </Link>
          </div>
        )}

        {isError && !notFound && (
          <div className="rounded-lg border border-cream-200 bg-white px-6 py-12 text-center shadow-sm">
            <p className="mb-2 text-rust-500">We couldn't load this invoice.</p>
            <p className="text-sm text-brown-400">
              Try refreshing the page in a moment.
            </p>
          </div>
        )}

        {data && (
          <>
            {/* Header */}
            <section className="mb-8 rounded-lg border border-cream-200 bg-white p-8 shadow-sm">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <h1 className="font-display text-3xl font-bold tracking-tight">
                      Invoice #{data.invoiceNumber}
                    </h1>
                    <span
                      className={
                        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold " +
                        STATUS_PILL_CLASS[data.status]
                      }
                    >
                      {STATUS_LABEL[data.status]}
                    </span>
                    {data.status !== "paid" && data.status !== "voided" && (
                      <span
                        className={
                          "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold " +
                          BUCKET_PILL_CLASS[data.bucket]
                        }
                      >
                        {BUCKET_LABEL[data.bucket]}
                      </span>
                    )}
                  </div>
                  <p className="text-brown-400">
                    {data.projectName} ·{" "}
                    <Link
                      to={`/orders/${data.orderId}`}
                      className="font-semibold text-ink underline-offset-2 hover:underline"
                    >
                      Order #{data.orderNumber}
                    </Link>
                    {" · "}
                    {data.invoiceType === "deposit" ? "Invoice · Prepay" : "Invoice"}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Balance due
                  </div>
                  <div className="font-display text-3xl font-bold tracking-tight text-brown-500 tabular-nums">
                    {formatUsd(outstanding)}
                  </div>
                </div>
              </div>

              <dl className="grid grid-cols-2 gap-y-4 text-sm sm:grid-cols-4">
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Dated
                  </dt>
                  <dd className="text-brown-500">
                    {formatDate(data.invoiceDate)}
                  </dd>
                </div>
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Terms
                  </dt>
                  <dd className="text-brown-500">{termsLabel(data.terms)}</dd>
                </div>
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Sent
                  </dt>
                  <dd className="text-brown-500">{formatDate(data.sentAt)}</dd>
                </div>
                <div>
                  <dt className="mb-1 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    Paid
                  </dt>
                  <dd className="text-brown-500">{formatDate(data.paidAt)}</dd>
                </div>
              </dl>
            </section>

            {/* Items — the client invoice document's lines (/i/:uuid). */}
            <section className="mb-8">
              <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
                Items
              </h2>
              {data.lines.length === 0 ? (
                <p className="rounded-lg border border-cream-200 bg-white px-6 py-10 text-center text-brown-300 shadow-sm">
                  No line items on this invoice.
                </p>
              ) : (
                <div className="space-y-4">
                  {data.lines.map((line) => (
                    <InvoiceLineCard key={line.id} line={line} />
                  ))}
                </div>
              )}
            </section>

            {/* Amounts breakdown */}
            <section className="mb-8 rounded-lg border border-cream-200 bg-white p-8 shadow-sm">
              <h2 className="mb-6 font-display text-xl font-bold tracking-tight">
                Amounts
              </h2>
              <dl className="space-y-3 text-brown-500">
                <div className="flex justify-between border-b border-cream-100 pb-2">
                  <dt>Subtotal</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatUsd(data.subtotal)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-cream-100 pb-2">
                  <dt>Tax</dt>
                  <dd className="font-semibold tabular-nums">
                    {formatUsd(data.tax)}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-cream-100 pb-2">
                  <dt>Total</dt>
                  <dd className="font-display text-lg font-bold tabular-nums">
                    {formatUsd(data.total)}
                  </dd>
                </div>
                {Number.parseFloat(data.depositApplied || "0") > 0 && (
                  <div className="flex justify-between border-b border-cream-100 pb-2">
                    <dt>Deposit applied</dt>
                    <dd className="font-semibold text-brown-400 tabular-nums">
                      − {formatUsd(data.depositApplied)}
                    </dd>
                  </div>
                )}
                {/* Ledgered payments — without this row the visible math
                    (Total − Deposit) wouldn't equal Balance due after a
                    part payment; the netting must be shown, not implied. */}
                {paymentsReceived > 0 && (
                  <div className="flex justify-between border-b border-cream-100 pb-2">
                    <dt>Payments received</dt>
                    <dd className="font-semibold text-brown-400 tabular-nums">
                      − {formatUsd(paymentsReceived.toFixed(2))}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between pt-2">
                  <dt className="font-display text-lg font-bold tracking-tight">
                    Balance due
                  </dt>
                  <dd className="font-display text-2xl font-bold tracking-tight text-rust-500 tabular-nums">
                    {formatUsd(outstanding)}
                  </dd>
                </div>
              </dl>
            </section>

            {/* Pay CTA */}
            {owes && (
              <section className="mb-8 rounded-lg border border-peach-300 bg-peach-50 p-8 shadow-sm print:hidden">
                {canPay ? (
                  <>
                    <h2 className="mb-2 font-display text-xl font-bold tracking-tight">
                      Pay this invoice
                    </h2>
                    <p className="mb-6 text-brown-500">
                      You'll be redirected to a secure Stripe checkout page.
                      Your balance of{" "}
                      <span className="font-semibold tabular-nums">
                        {formatUsd(outstanding)}
                      </span>{" "}
                      will be charged to your card.
                    </p>
                    {payError && (
                      <div className="mb-4 rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
                        {payError}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => void onPayClick()}
                      disabled={payStarting}
                      className="rounded-md bg-ink px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
                    >
                      {payStarting
                        ? "Starting checkout…"
                        : `Pay ${formatUsd(outstanding)} now`}
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="mb-2 font-display text-lg font-bold tracking-tight">
                      How to pay
                    </h2>
                    <p className="text-brown-500">
                      To pay this invoice, reply to your account manager —
                      they'll arrange payment.
                    </p>
                  </>
                )}
              </section>
            )}

            {/* Payments ledger */}
            {data.payments.length > 0 && (
              <section className="mb-8">
                <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
                  Payments
                </h2>
                <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
                        <th className="px-6 py-3">Paid on</th>
                        <th className="px-6 py-3">Method</th>
                        <th className="px-6 py-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((p) => (
                        <tr
                          key={p.id}
                          className="border-b border-cream-100 last:border-b-0"
                        >
                          <td className="px-6 py-3 text-brown-500">
                            {formatDate(p.paidAt)}
                          </td>
                          <td className="px-6 py-3 text-brown-400">
                            {PAYMENT_METHOD_LABEL[p.paymentMethod] ??
                              p.paymentMethod}
                          </td>
                          <td className="px-6 py-3 text-right font-semibold tabular-nums text-brown-500">
                            {formatUsd(p.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
    </PortalLayout>
  );
}

/**
 * One invoice line — the same rows and money math as the client invoice
 * document's LineCard (apps/web PublicInvoicePage): a row per variant at the
 * variant's price (falling back to the line price), and a line total of line
 * price × total units. The invoice record's own totals (Amounts, below) stay
 * the authoritative figures.
 */
function InvoiceLineCard({ line }: { line: PortalInvoiceLine }) {
  const productName =
    line.productSnapshot?.name ||
    line.description ||
    (line.serviceType === "service" ? "Service" : "Item");
  const imageUrl = lineImageUrl(line);
  const unitPrice = Number(line.unitPrice);
  const rows =
    line.variants.length > 0
      ? line.variants.map((v) => ({
          id: v.id,
          label: variantLabel(v),
          qty: v.quantity,
          unit: Number(v.unitPrice ?? line.unitPrice),
        }))
      : [
          {
            id: `${line.id}-flat`,
            label: line.description ?? "Standard",
            qty: line.quantityTotal,
            unit: unitPrice,
          },
        ];
  const totalUnits = rows.reduce((acc, r) => acc + r.qty, 0);
  // The rows above added up — never the line's own price × every unit, which
  // can be out of date (Stephanie 2026-11-02: an invoice showed $18,425 for $5,500).
  const lineTotal =
    Math.round(rows.reduce((acc, r) => acc + (Number.isFinite(r.unit) ? r.unit * r.qty : 0), 0) * 100) / 100;
  // Shipping and services have no picture (Stephanie 2026-11-02).
  const isService = line.serviceType === "shipping" || line.serviceType === "service";

  return (
    <article className="rounded-lg border border-cream-200 bg-white p-6 shadow-sm">
      <h3 className="mb-1 font-display text-lg font-bold tracking-tight text-brown-500">
        {productName}
      </h3>
      {line.productSnapshot?.sku && (
        <p className="mb-2 text-xs text-brown-300">{line.productSnapshot.sku}</p>
      )}
      {line.description && line.description !== productName && (
        <p className="mb-4 whitespace-pre-wrap text-sm text-brown-400">
          {line.description}
        </p>
      )}
      <div className={isService ? "grid gap-6" : "grid gap-6 md:grid-cols-[140px_1fr]"}>
        {!isService && (
        <div className="aspect-square overflow-hidden rounded-md border border-cream-200 bg-white">
          {imageUrl ? (
            // The whole mock-up shows, never cropped (Stephanie 2026-11-02).
            <img
              src={imageUrl}
              alt={productName}
              loading="lazy"
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-cream-100 text-[0.6875rem] uppercase tracking-wide text-brown-300">
              No image
            </div>
          )}
        </div>
        )}
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
              <th className="py-2">Item</th>
              <th className="py-2 text-right">Qty</th>
              <th className="py-2 text-right">Price</th>
              <th className="py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-cream-100">
                <td className="py-2 text-brown-500">{r.label}</td>
                <td className="py-2 text-right tabular-nums text-brown-500">
                  {r.qty.toLocaleString()}
                </td>
                <td className="py-2 text-right tabular-nums text-brown-400">
                  {formatUsd(String(r.unit))}
                </td>
                <td className="py-2 text-right tabular-nums text-brown-500">
                  {formatUsd(String(r.unit * r.qty))}
                </td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="py-2 text-brown-500">Total units</td>
              <td className="py-2 text-right tabular-nums text-brown-500">
                {totalUnits.toLocaleString()}
              </td>
              <td />
              <td className="py-2 text-right tabular-nums text-rust-500">
                {formatUsd(String(lineTotal))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  );
}
